/**
 * Accounts (Figma 09 · Accounts across brands / 22): every brand's social
 * accounts in one place, read only. Built from each brand's own connections
 * read; connecting and removing happens on the brand's Social accounts page.
 * Not built, no backend yet: "waiting for the client" requests.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { useQueries } from "@tanstack/react-query";
import { ArrowRight, TriangleAlert } from "lucide-react";
import { axiosInstance } from "@/lib/api";
import { useGetBrands, type Brand } from "@/hooks/useGetBrands";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import type { SocialConnection } from "@/app/routes/dashboard/settings/api/useSocialConnections";
import { BrandMark } from "../../kit/BrandPicker";
import { EmptyState, ErrorState } from "../../ViewStates";
import {
  DetailCard,
  DetailHead,
  IndexCard,
  IndexRow,
  PageHead,
  QuietNote,
  SettingsPage,
  Split,
  SplitLoading,
  TextAction,
} from "../parts/SettingsParts";
import { useWorkspaceHead } from "../parts/scope";
import { listJoin, plural } from "../parts/settingsFormat";
import { PROVIDERS, providerInfo } from "./socialProviders";

export type BrandAccountsSummary = {
  publishing: number;
  total: number;
  attention: string[];
  line: string;
};

export function summarise(connections: SocialConnection[] | undefined): BrandAccountsSummary {
  const list = (connections ?? []).filter((c) => PROVIDERS.some((p) => p.provider === c.provider));
  const accounts = list.flatMap((c) => c.accounts.map((a) => ({ a, c })));
  const attention = [...new Set(list.filter((c) => c.status === "NEEDS_REAUTH").map((c) => providerInfo(c.provider).label))];
  const publishing = accounts.filter(({ c }) => c.status !== "NEEDS_REAUTH").length;
  const line =
    accounts.length === 0
      ? "No accounts yet"
      : attention.length
        ? `${listJoin(attention)} ${attention.length === 1 ? "needs" : "need"} reconnecting`
        : `${publishing} of ${accounts.length} publishing`;
  return { publishing, total: accounts.length, attention, line };
}

/** Each brand's connections, read in parallel; the same cache keys the brand page uses. */
export function useBrandsConnections(brands: Brand[]) {
  return useQueries({
    queries: brands.map((brand) => ({
      queryKey: ["socialConnections", brand.id] as const,
      queryFn: async () => {
        const res = await axiosInstance.get<SocialConnection[]>(`/social/brand/${brand.id}/connections`);
        return res.data;
      },
    })),
  });
}

export default function AccountsPage() {
  const head = useWorkspaceHead();
  const navigate = useNavigate();
  const setSelectedBrandId = useWorkspaceStore((s) => s.setSelectedBrandId);
  const brandsQuery = useGetBrands();
  const brands = brandsQuery.data ?? [];
  const results = useBrandsConnections(brands);
  const [picked, setPicked] = useState<string | null>(null);

  const summaries = new Map(brands.map((b, i) => [b.id, summarise(results[i]?.data)]));
  const needing = brands.filter((b) => (summaries.get(b.id)?.attention.length ?? 0) > 0).length;
  const publishing = [...summaries.values()].reduce((n, s) => n + s.publishing, 0);
  const selected = brands.find((b) => b.id === picked) ?? brands[0];
  const selectedIndex = selected ? brands.indexOf(selected) : -1;
  const selectedConnections = selectedIndex >= 0 ? results[selectedIndex]?.data : undefined;

  const openBrand = (brand: Brand) => {
    setSelectedBrandId(brand.id);
    navigate("/dashboard/settings?page=social-accounts");
  };

  const status = `Every brand’s social accounts in one place.${
    needing ? ` ${plural(needing, "brand")} ${needing === 1 ? "needs" : "need"} attention;` : ""
  } ${plural(publishing, "account")} publish.`;

  return (
    <SettingsPage>
      <PageHead {...head} title="Accounts" status={brandsQuery.isSuccess ? status : undefined} />
      {brandsQuery.isLoading ? (
        <SplitLoading caption="Loading every brand’s accounts" />
      ) : brandsQuery.isError ? (
        <ErrorState title="The accounts did not load" body="Creo could not read them. Nothing has changed." onRetry={() => void brandsQuery.refetch()} />
      ) : brands.length === 0 ? (
        <EmptyState title="No brands yet" body="Accounts belong to one brand each. Make a brand to connect its accounts." />
      ) : (
        <Split
          backLabel="Accounts"
          index={
            <IndexCard>
              {brands.map((brand, i) => {
                const s = summaries.get(brand.id)!;
                const loading = results[i]?.isLoading;
                return (
                  <IndexRow
                    key={brand.id}
                    lead={<BrandMark brand={brand} size={36} />}
                    title={brand.name}
                    sub={loading ? "Reading…" : s.line}
                    subTone={s.attention.length ? "alert" : "muted"}
                    trail={s.attention.length ? <TriangleAlert aria-hidden className="size-3.5 shrink-0 text-co-alert-ink" /> : undefined}
                    active={brand.id === selected?.id}
                    onPress={() => setPicked(brand.id)}
                  />
                );
              })}
            </IndexCard>
          }
          detail={
            selected ? (
              <DetailCard>
                <DetailHead
                  lead={<BrandMark brand={selected} size={56} />}
                  title={selected.name}
                  sub={summaries.get(selected.id)?.line}
                />
                <div className="-mx-2 flex flex-col">
                  {PROVIDERS.flatMap((info) => {
                    const connection = selectedConnections?.find((c) => c.provider === info.provider);
                    const Glyph = info.icon;
                    const accounts = connection?.accounts ?? [];
                    const needs = connection?.status === "NEEDS_REAUTH";
                    const rowsFor = accounts.length ? accounts : [null];
                    return rowsFor.map((account) => (
                      <div key={account?.id ?? info.provider} className="flex items-center gap-3 rounded-row px-2 py-2.5">
                        <span className="grid size-9 shrink-0 place-items-center rounded-full border border-co-line2 bg-co-surface-solid">
                          <Glyph className="size-4 text-co-text2" />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className="truncate text-[13.5px] font-semibold text-co-text">
                            {account ? account.name || account.username || info.label : info.label}
                          </span>
                          <span className={needs ? "text-[12px] text-co-alert-ink" : "text-[12px] text-co-muted"}>
                            {account
                              ? `${info.accountNoun} · ${needs ? "Needs attention" : "Connected"}${
                                  connection && connection.pendingPublicationCount > 0
                                    ? ` · ${plural(connection.pendingPublicationCount, "post")} ${needs ? "held" : "scheduled"}`
                                    : ""
                                }`
                              : "Not connected"}
                          </span>
                        </span>
                      </div>
                    ));
                  })}
                </div>
                <QuietNote>Accounts belong to one brand each. To connect, reconnect or remove one, open the brand.</QuietNote>
                <TextAction icon={ArrowRight} onPress={() => openBrand(selected)}>
                  Open {selected.name}’s social accounts
                </TextAction>
              </DetailCard>
            ) : null
          }
        />
      )}
    </SettingsPage>
  );
}
